import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import Button from '../Button';
import useSwipeToClose from '../hooks/useSwipeToClose';
import './ModalPopup.css';
import { drawerDurationMs } from '../utils/cssDuration';

/**
 * ModalPopup
 *
 * Shared modal shell used by the storefront's form popups:
 *  - Desktop → centered popup with an optional banner image on the left
 *  - Mobile  → bottom-sheet drawer with swipe-to-dismiss
 *
 * Owns the chrome only — overlay, panel, banner, close button, swipe handle,
 * ESC / overlay-click dismissal, body scroll lock and the enter/close
 * animation. Callers render just their content as `children`, which lands
 * inside the padded, max-width wrapper.
 *
 * Extracted from EditOrderLogin, which was in turn modelled on SchoolSelector.
 * Both still carry their own copies of this markup; they're candidates to
 * migrate onto this shell.
 */
const ModalPopup = ({
  isOpen = false,
  onClose,
  banner = null,
  ariaLabel = '',
  closeAriaLabel = 'Close',
  preview = false,
  lockScroll = true,
  className = '',
  children,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isEntered, setIsEntered] = useState(false);

  const overlayRef = useRef(null);
  const enteredTimer = useRef(null);
  const closeTimer = useRef(null);

  /* ── Scroll lock + enter animation ── */
  useEffect(() => {
    if (preview) return undefined;

    if (isOpen) {
      setIsClosing(false);
      if (lockScroll) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }
      // Once the enter animation has played, drop it so the swipe transform
      // isn't fighting a running keyframe.
      enteredTimer.current = setTimeout(() => setIsEntered(true), drawerDurationMs() + 50);
    } else {
      setIsEntered(false);
      if (lockScroll) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    }

    return () => clearTimeout(enteredTimer.current);
  }, [isOpen, preview, lockScroll]);

  useEffect(() => () => {
    clearTimeout(closeTimer.current);
    if (lockScroll && !preview) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }, [lockScroll, preview]);

  const animateClose = useCallback(() => {
    if (preview) return;
    setIsClosing(true);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, drawerDurationMs());
  }, [onClose, preview]);

  const { containerRef: mobileDrawerRef, dragOffset, isDragging, DragHandle } = useSwipeToClose(
    animateClose,
    isOpen && !preview,
  );

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) animateClose();
  };

  /* ── ESC to close ── */
  useEffect(() => {
    if (preview || !isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') animateClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, preview, animateClose]);

  if (!isOpen && !isClosing && !preview) return null;

  const drawerStyle = isDragging ? { transform: `translateY(${dragOffset}px)` } : undefined;

  const renderInner = () => (
    <div className="ds-modal-popup__container">
      <Button
        size="tertiary"
        variant="text"
        iconOnly
        iconLeft={<X size={20} />}
        aria-label={closeAriaLabel}
        className="ds-modal-popup__close"
        onClick={animateClose}
      />

      <div className="ds-modal-popup__wrapper">
        {children}
      </div>
    </div>
  );

  return (
    <div
      ref={overlayRef}
      className={`ds-modal-popup__overlay ${isClosing ? 'ds-modal-popup__overlay--closing' : ''} ${preview ? 'ds-modal-popup__overlay--preview' : ''} ${className}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      {/* ── Desktop: centered popup ── */}
      <div
        className={`ds-modal-popup ds-modal-popup--desktop ${isClosing ? 'ds-modal-popup--closing' : ''} ${isEntered ? 'ds-modal-popup--entered' : ''}`}
      >
        {banner && (
          <div className="ds-modal-popup__banner">
            <img src={banner} alt="" className="ds-modal-popup__banner-img" />
          </div>
        )}

        <div className="ds-modal-popup__content">
          {renderInner()}
        </div>
      </div>

      {/* ── Mobile: bottom sheet ── */}
      <div
        ref={mobileDrawerRef}
        className={`ds-modal-popup ds-modal-popup--mobile ${isClosing ? 'ds-modal-popup--closing' : ''} ${isEntered ? 'ds-modal-popup--entered' : ''}`}
        style={drawerStyle}
      >
        <DragHandle className="ds-modal-popup__swipe-handle" />

        <div className="ds-modal-popup__content">
          {renderInner()}
        </div>
      </div>
    </div>
  );
};

ModalPopup.propTypes = {
  /** Whether the popup/drawer is open */
  isOpen: PropTypes.bool,
  /** Called once the close animation has finished */
  onClose: PropTypes.func,
  /** Optional banner image (desktop only) */
  banner: PropTypes.string,
  /** Accessible label for the dialog */
  ariaLabel: PropTypes.string,
  /** Accessible label for the close button */
  closeAriaLabel: PropTypes.string,
  /** Static preview mode (no animation / scroll lock / ESC) */
  preview: PropTypes.bool,
  /** Lock body scroll while open */
  lockScroll: PropTypes.bool,
  /** Additional class name on the overlay */
  className: PropTypes.string,
  /** Popup content — rendered inside the padded wrapper */
  children: PropTypes.node,
};

export default ModalPopup;
